import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Zap, Users } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Props interface
interface ComingSoonProps {
  language?: string;
}

// Translations for all 12 languages
const TRANSLATIONS: Record<string, {
  mainHeadingPart1: string;
  mainHeadingPart2: string;
  subHeading: string;
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  feature1Title: string;
  feature1Description: string;
  feature2Title: string;
  feature2Description: string;
  feature3Title: string;
  feature3Description: string;
  newsletterTitle: string;
  newsletterDescription: string;
  emailPlaceholder: string;
  notifyMe: string;
  thankYouMessage: string;
}> = {
  en: {
    mainHeadingPart1: "Our Franchise-",
    mainHeadingPart2: "System",
    subHeading: "Coming to the Markets Worldwide",
    days: "Days",
    hours: "Hours",
    minutes: "Minutes",
    seconds: "Seconds",
    feature1Title: "German Engineering",
    feature1Description: "Premium quality products with renowned reliability and precision engineered in Germany.",
    feature2Title: "Innovative System",
    feature2Description: "Benefit from our cutting-edge business model and proven operational methodology.",
    feature3Title: "Comprehensive Support",
    feature3Description: "Ongoing training, marketing assistance, and expert support from our experienced team.",
    newsletterTitle: "Join Our Exclusive Launch List",
    newsletterDescription: "Be among the first to receive our franchise information package and early access opportunities.",
    emailPlaceholder: "Enter your email address",
    notifyMe: "Notify Me",
    thankYouMessage: "Thank you for your interest! We'll notify you when we launch."
  },
  de: {
    mainHeadingPart1: "Unser Franchise-",
    mainHeadingPart2: "System",
    subHeading: "Kommt auf die Märkte weltweit",
    days: "Tage",
    hours: "Stunden",
    minutes: "Minuten",
    seconds: "Sekunden",
    feature1Title: "Deutsche Ingenieurskunst",
    feature1Description: "Hochwertige Produkte mit bekannter Zuverlässigkeit und Präzision, entwickelt in Deutschland.",
    feature2Title: "Innovatives System",
    feature2Description: "Profitieren Sie von unserem zukunftsweisenden Geschäftsmodell und bewährter Betriebsmethodik.",
    feature3Title: "Umfassende Unterstützung",
    feature3Description: "Laufende Schulungen, Marketinghilfe und fachkundige Unterstützung von unserem erfahrenen Team.",
    newsletterTitle: "Treten Sie unserer exklusiven Startliste bei",
    newsletterDescription: "Seien Sie unter den Ersten, die unser Franchise-Informationspaket und frühzeitige Zugangsmöglichkeiten erhalten.",
    emailPlaceholder: "Geben Sie Ihre E-Mail-Adresse ein",
    notifyMe: "Benachrichtigen Sie mich",
    thankYouMessage: "Vielen Dank für Ihr Interesse! Wir benachrichtigen Sie bei der Einführung."
  },
  es: {
    mainHeadingPart1: "Nuestro Franchise-",
    mainHeadingPart2: "Sistema",
    subHeading: "Llega a los mercados mundiales",
    days: "Días",
    hours: "Horas",
    minutes: "Minutos",
    seconds: "Segundos",
    feature1Title: "Ingeniería Alemana",
    feature1Description: "Productos de calidad superior con reconocida fiabilidad y precisión diseñados en Alemania.",
    feature2Title: "Sistema Innovador",
    feature2Description: "Benefíciese de nuestro modelo de negocio de vanguardia y metodología operativa comprobada.",
    feature3Title: "Soporte Completo",
    feature3Description: "Capacitación continua, asistencia en marketing y soporte experto de nuestro equipo experimentado.",
    newsletterTitle: "Únase a nuestra lista de lanzamiento exclusiva",
    newsletterDescription: "Esté entre los primeros en recibir nuestro paquete de información de franquicia y oportunidades de acceso anticipado.",
    emailPlaceholder: "Ingrese su dirección de correo electrónico",
    notifyMe: "Notifíqueme",
    thankYouMessage: "¡Gracias por su interés! Le notificaremos cuando lancemos."
  },
  fr: {
    mainHeadingPart1: "Notre Franchise-",
    mainHeadingPart2: "Système",
    subHeading: "Arrive sur les marchés mondiaux",
    days: "Jours",
    hours: "Heures",
    minutes: "Minutes",
    seconds: "Secondes",
    feature1Title: "Ingénierie Allemande",
    feature1Description: "Produits de qualité supérieure avec une fiabilité et une précision reconnues, conçus en Allemagne.",
    feature2Title: "Système Innovant",
    feature2Description: "Bénéficiez de notre modèle commercial de pointe et de notre méthodologie opérationnelle éprouvée.",
    feature3Title: "Support Complet",
    feature3Description: "Formation continue, assistance marketing et soutien expert de notre équipe expérimentée.",
    newsletterTitle: "Rejoignez notre liste de lancement exclusive",
    newsletterDescription: "Soyez parmi les premiers à recevoir notre package d'informations sur la franchise et des opportunités d'accès anticipé.",
    emailPlaceholder: "Entrez votre adresse e-mail",
    notifyMe: "Notifiez-moi",
    thankYouMessage: "Merci pour votre intérêt ! Nous vous notifierons lors du lancement."
  },
  it: {
    mainHeadingPart1: "Il nostro Franchise-",
    mainHeadingPart2: "Sistema",
    subHeading: "Arriva sui mercati mondiali",
    days: "Giorni",
    hours: "Ore",
    minutes: "Minuti",
    seconds: "Secondi",
    feature1Title: "Ingegneria Tedesca",
    feature1Description: "Prodotti di qualità premium con affidabilità e precisione rinomate, progettati in Germania.",
    feature2Title: "Sistema Innovativo",
    feature2Description: "Beneficia del nostro modello di business all'avanguardia e della metodologia operativa collaudata.",
    feature3Title: "Supporto Completo",
    feature3Description: "Formazione continua, assistenza marketing e supporto esperto dal nostro team qualificato.",
    newsletterTitle: "Unisciti alla nostra lista di lancio esclusiva",
    newsletterDescription: "Sii tra i primi a ricevere il nostro pacchetto informativo sulla franchigia e opportunità di accesso anticipato.",
    emailPlaceholder: "Inserisci il tuo indirizzo email",
    notifyMe: "Notificami",
    thankYouMessage: "Grazie per il tuo interesse! Ti notificheremo al lancio."
  },
  nl: {
    mainHeadingPart1: "Ons Franchise-",
    mainHeadingPart2: "Systeem",
    subHeading: "Komt naar de wereldmarkten",
    days: "Dagen",
    hours: "Uren",
    minutes: "Minuten",
    seconds: "Seconden",
    feature1Title: "Duitse Techniek",
    feature1Description: "Hoogwaardige producten met bekende betrouwbaarheid en precisie, ontworpen in Duitsland.",
    feature2Title: "Innovatief Systeem",
    feature2Description: "Profiteer van ons geavanceerde bedrijfsmodel en bewezen operationele methodologie.",
    feature3Title: "Uitgebreide Ondersteuning",
    feature3Description: "Doorlopende training, marketingondersteuning en deskundige hulp van ons ervaren team.",
    newsletterTitle: "Sluit je aan bij onze exclusieve lanceringslijst",
    newsletterDescription: "Wees een van de eersten die ons franchise-informatiepakket en vroege toegangsmogelijkheden ontvangt.",
    emailPlaceholder: "Voer je e-mailadres in",
    notifyMe: "Houd mij op de hoogte",
    thankYouMessage: "Bedankt voor je interesse! We stellen je op de hoogte bij de lancering."
  },
  sa: {
    mainHeadingPart1: "نظام الامتياز-",
    mainHeadingPart2: "الخاص بنا",
    subHeading: "يصل إلى الأسواق العالمية",
    days: "أيام",
    hours: "ساعات",
    minutes: "دقائق",
    seconds: "ثوانٍ",
    feature1Title: "الهندسة الألمانية",
    feature1Description: "منتجات عالية الجودة ذات موثوقية ودقة مشهورة، مصممة في ألمانيا.",
    feature2Title: "نظام مبتكر",
    feature2Description: "استفد من نموذج الأعمال المتطور لدينا والمنهجية التشغيلية المثبتة.",
    feature3Title: "دعم شامل",
    feature3Description: "تدريب مستمر، مساعدة تسويقية، ودعم خبير من فريقنا ذو الخبرة.",
    newsletterTitle: "انضم إلى قائمة إطلاقنا الحصرية",
    newsletterDescription: "كن من بين الأوائل الذين يتلقون حزمة معلومات الامتياز الخاصة بنا وفرص الوصول المبكر.",
    emailPlaceholder: "أدخل عنوان بريدك الإلكتروني",
    notifyMe: "أخبرني",
    thankYouMessage: "شكرًا لاهتمامك! سنُعلمك عند الإطلاق."
  },
  hk: {
    mainHeadingPart1: "我哋嘅特許經營-",
    mainHeadingPart2: "系統",
    subHeading: "即將進入全球市場",
    days: "日",
    hours: "小時",
    minutes: "分鐘",
    seconds: "秒",
    feature1Title: "德國工程",
    feature1Description: "優質產品，以德國聞名的可靠性和精準度設計。",
    feature2Title: "創新系統",
    feature2Description: "受惠於我哋尖端的商業模式同經過驗證嘅運營方法。",
    feature3Title: "全面支援",
    feature3Description: "持續培訓、市場推廣協助同我哋經驗豐富團隊嘅專家支援。",
    newsletterTitle: "加入我哋嘅獨家啟動名單",
    newsletterDescription: "成為首批收到我哋特許經營資訊包同早期參與機會嘅人。",
    emailPlaceholder: "輸入您嘅電郵地址",
    notifyMe: "通知我",
    thankYouMessage: "多謝您嘅興趣！我哋會喺推出時通知您。"
  },
  sg: {
    mainHeadingPart1: "我们的特许经营-",
    mainHeadingPart2: "系统",
    subHeading: "即将进入全球市场",
    days: "天",
    hours: "小时",
    minutes: "分钟",
    seconds: "秒",
    feature1Title: "德国工程",
    feature1Description: "优质产品，以德国著名的可靠性和精准度设计。",
    feature2Title: "创新系统",
    feature2Description: "受益于我们尖端的商业模式和经过验证的运营方法。",
    feature3Title: "全面支持",
    feature3Description: "持续培训、市场推广协助和我们经验丰富团队的专家支持。",
    newsletterTitle: "加入我们的独家启动名单",
    newsletterDescription: "成为首批收到我们特许经营信息包和早期参与机会的人。",
    emailPlaceholder: "输入您的电子邮件地址",
    notifyMe: "通知我",
    thankYouMessage: "感谢您的兴趣！我们会在推出时通知您。"
  },
  za: {
    mainHeadingPart1: "Ons Franchise-",
    mainHeadingPart2: "Stelsel",
    subHeading: "Kom na die wêreldmarkte",
    days: "Dae",
    hours: "Ure",
    minutes: "Minute",
    seconds: "Sekondes",
    feature1Title: "Duitse Ingenieurswese",
    feature1Description: "Premium kwaliteit produkte met bekende betroubaarheid en presisie, ontwerp in Duitsland.",
    feature2Title: "Innoverende Stelsel",
    feature2Description: "Baet by ons voorpunt besigheidsmodel en bewese operasionele metodologie.",
    feature3Title: "Omvattende Ondersteuning",
    feature3Description: "Deurlopende opleiding, bemarkingshulp en kundige ondersteuning van ons ervare span.",
    newsletterTitle: "Sluit aan by ons eksklusiewe lanseerlys",
    newsletterDescription: "Wees onder die eerstes om ons franchise-inligtingspakket en vroeë toeganggeleenthede te ontvang.",
    emailPlaceholder: "Voer jou e-posadres in",
    notifyMe: "Laat weet my",
    thankYouMessage: "Dankie vir jou belangstelling! Ons sal jou in kennis stel wanneer ons lanseer."
  },
  kr: {
    mainHeadingPart1: "우리의 프랜차이즈-",
    mainHeadingPart2: "시스템",
    subHeading: "전 세계 시장에 출시 예정",
    days: "일",
    hours: "시간",
    minutes: "분",
    seconds: "초",
    feature1Title: "독일 공학",
    feature1Description: "독일에서 설계된 유명한 신뢰성과 정밀도를 갖춘 프리미엄 품질 제품.",
    feature2Title: "혁신적인 시스템",
    feature2Description: "우리의 최첨단 비즈니스 모델과 입증된 운영 방법론의 혜택을 누리세요.",
    feature3Title: "종합 지원",
    feature3Description: "지속적인 교육, 마케팅 지원 및 경험이 풍부한 팀의 전문가 지원.",
    newsletterTitle: "우리의 독점 출시 목록에 가입하세요",
    newsletterDescription: "프랜차이즈 정보 패키지와 조기 접근 기회를 처음으로 받는 사람 중 하나가 되세요.",
    emailPlaceholder: "이메일 주소를 입력하세요",
    notifyMe: "알림 받기",
    thankYouMessage: "관심을 가져주셔서 감사합니다! 출시 시 알림을 드리겠습니다."
  },
  jp: {
    mainHeadingPart1: "私たちのフランチャイズ-",
    mainHeadingPart2: "システム",
    subHeading: "世界市場に登場",
    days: "日",
    hours: "時間",
    minutes: "分",
    seconds: "秒",
    feature1Title: "ドイツ工学",
    feature1Description: "ドイツで設計された、信頼性と精密さで知られるプレミアム品質の製品。",
    feature2Title: "革新的なシステム",
    feature2Description: "最先端のビジネスモデルと実証済みの運用方法論の恩恵を受けます。",
    feature3Title: "包括的サポート",
    feature3Description: "継続的なトレーニング、マーケティング支援、経験豊富なチームからの専門サポート。",
    newsletterTitle: "私たちの独占ローンチリストに参加",
    newsletterDescription: "フランチャイズ情報パッケージと早期アクセス機会を最初に受け取る一人になりましょう。",
    emailPlaceholder: "メールアドレスを入力してください",
    notifyMe: "通知する",
    thankYouMessage: "ご興味ありがとうございます！ローンチ時に通知いたします。"
  }
};

// Utility to generate meta description
const getMetaDescription = (translations) => {
  const description = `${translations.subHeading} ${translations.feature1Description}`;
  return description.length > 160 ? description.slice(0, 157) + '...' : description;
};

const ComingSoon: React.FC<ComingSoonProps> = ({ language = 'en' }) => {
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const launchDate = new Date('2025-07-18T00:00:00');
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  useEffect(() => {
    const countdown = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      setDays(Math.floor(distance / (1000 * 60 * 60 * 24)));
      setHours(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      setMinutes(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
      setSeconds(Math.floor((distance % (1000 * 60)) / 1000));

      if (distance < 0) clearInterval(countdown);
    }, 1000);

    return () => clearInterval(countdown);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setEmail('');
  };

  const franchiseText = translations.mainHeadingPart1.replace('-', '');
  const systemText = translations.mainHeadingPart2;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`${franchiseText}${systemText} | ${translations.subHeading}`}</title>
        <meta name="description" content={getMetaDescription(translations)} />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="German franchise, quality products, bulk orders, global markets" />
        <link rel="canonical" href={`https://made-in-germany.uk/${language === 'en' ? '' : language}`} />
        {Object.keys(TRANSLATIONS).map((lang) => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang === 'en' ? 'en-gb' : lang}
            href={`https://made-in-germany.uk/${lang === 'en' ? '' : lang}`}
          />
        ))}
        <meta property="og:title" content={`${franchiseText}${systemText}`} />
        <meta property="og:description" content={getMetaDescription(translations)} />
        <meta property="og:url" content={`https://made-in-germany.uk/${language === 'en' ? '' : language}`} />
        <meta property="og:image" content="https://made-in-germany.uk/og-image.webp" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#FFFFFF] font-sans">
        <div className="w-full h-1 bg-[#0B111F] mb-8" />
        <div className="max-w-7xl mx-auto px-0">
          <header className="container mx-auto px-6 py-16 text-center">
            <motion.h1
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="text-5xl md:text-6xl font-extrabold text-[#0B111F] mb-6 leading-tight"
            >
              <span className="text-[#0B111F]">{franchiseText.split('Franchise')[0]}</span>
              <span className="text-[#d00b10]">Franchise</span>
              <span className="text-[#ffcc00]">{systemText}</span>
              <br />
              {translations.subHeading}
            </motion.h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {translations.feature1Description}
            </p>
          </header>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16 mx-auto max-w-4xl">
            {[
              { value: days, label: translations.days },
              { value: hours, label: translations.hours },
              { value: minutes, label: translations.minutes },
              { value: seconds, label: translations.seconds },
            ].map((item, index) => (
              <motion.article
                key={index}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative bg-[#0B111F] rounded-2xl p-6 border border-gray-800 backdrop-blur-xl overflow-hidden group flex flex-col items-center"
              >
                <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
                <div className="relative z-10">
                  <span className="text-4xl md:text-5xl font-bold text-white">{item.value}</span>
                  <span className="text-gray-200 font-medium mt-1 block">{item.label}</span>
                </div>
              </motion.article>
            ))}
          </section>

          <section className="grid md:grid-cols-3 gap-8 mb-16 mx-auto max-w-6xl">
            {[
              {
                icon: <CheckCircle className="h-12 w-12" aria-hidden="true" />,
                title: translations.feature1Title,
                description: translations.feature1Description,
              },
              {
                icon: <Zap className="h-12 w-12" aria-hidden="true" />,
                title: translations.feature2Title,
                description: translations.feature2Description,
              },
              {
                icon: <Users className="h-12 w-12" aria-hidden="true" />,
                title: translations.feature3Title,
                description: translations.feature3Description,
              },
            ].map((feature, index) => (
              <motion.article
                key={index}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative bg-[#0B111F] rounded-2xl p-6 border border-gray-oji800 backdrop-blur-xl overflow-hidden group flex flex-col items-center"
              >
                <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
                <div className="relative z-10 flex flex-col space-y-4 items-center">
                  {feature.icon}
                  <h2 className="text-xl font-bold text-white">{feature.title}</h2>
                  <p className="text-gray-200">{feature.description}</p>
                </div>
              </motion.article>
            ))}
          </section>

          <section className="relative bg-[#0B111F] rounded-2xl p-8 border border-gray-800 backdrop-blur-xl overflow-hidden max-w-3xl w-full mx-auto mb-16">
            <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
            <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-6 text-white">{translations.newsletterTitle}</h2>
              <p className="text-gray-200 mb-6">{translations.newsletterDescription}</p>
              <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-3">
                <input
                  type="email"
                  placeholder={translations.emailPlaceholder}
                  className="flex-grow px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d00b10] focus:border-transparent"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-label={translations.emailPlaceholder}
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="bg-white text-[#0B111F] font-semibold py-3 px-8 rounded-lg transition-all duration-300 hover:bg-gray-100 shadow-lg"
                >
                  {translations.notifyMe}
                </motion.button>
              </form>
              {submitted && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg"
                  role="alert"
                >
                  {translations.thankYouMessage}
                </motion.div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ComingSoon;